import React from 'react';
import { Box, Paper, Typography } from '@mui/material';
import { PauseCircleOutline as PauseIcon } from '@mui/icons-material';
import { useTenant } from '../../contexts/TenantContext';
import { useAdminAccess } from '../../hooks/useAdminAccess';

/**
 * Community suspension gate.
 *
 * When a super-admin has temporarily turned a community off (`config.suspended`),
 * everyone in that community sees a "temporarily unavailable" message instead of
 * the app. Super-admins are exempt so they can still manage/restore the tenant.
 * Server-side enforcement (enforceBillingAccess middleware) is the real block;
 * this is the UX layer. Takes precedence over the billing gate.
 */
export const SuspendedGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { config } = useTenant();
  const { isSuperAdmin } = useAdminAccess();

  if (!config.suspended || isSuperAdmin()) return <>{children}</>;

  const communityName = config.name || config.appName || 'This community';

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '60vh',
        px: 2,
      }}
    >
      <Paper sx={{ p: { xs: 3, sm: 5 }, maxWidth: 520, textAlign: 'center' }}>
        <PauseIcon color="warning" sx={{ fontSize: 56, mb: 1 }} />
        <Typography variant="h5" fontWeight={700} gutterBottom>
          Temporarily unavailable
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {communityName} is temporarily unavailable. Please reach out to your community
          administrator for more information.
        </Typography>
      </Paper>
    </Box>
  );
};

export default SuspendedGate;
